import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { LocationQuery, useRoute, useRouter } from 'vue-router'

import { DEFAULT_CLIDS } from '@/constants/general'
import { ExportMode, type PlatformType } from '@/types'
import { processLandingAttributes } from '@/utils/affiliate'

export type ErrorStatus = 404 | 500
export type PageStatus = 200 | 301 | 302 | ErrorStatus

const DEFAULT_PLATFORM = 'desktop'
const WIDGET_ID_PARAM_NAME = 'widget_id'
const CLID_PARAM = 'clid'
const EXPORT_MODE_PARAM = 'export_mode'
const GAME_DISTRIBUTION_KEY = 'use_gd_ads'

export const useAppStore = defineStore('app', () => {
    const pageStatus = ref<PageStatus>(200)
    const targetURL = ref<string>('')
    const platform = ref<PlatformType>(DEFAULT_PLATFORM)
    const host = ref<string>()
    const userAcquisitionParams = ref<Record<string, string>>({})
    const query = ref<LocationQuery>()
    const isGameDistribution = computed(() => query.value?.[GAME_DISTRIBUTION_KEY])
    const isDebugMode = computed(() => query.value?.debug === 'true')

    const route = useRoute()
    const { landingClid, landingWidgetId } = processLandingAttributes(route)

    const widgetId = computed(() => {
        return (query.value?.[WIDGET_ID_PARAM_NAME] || landingWidgetId) as string
    })
    const clid = computed(() => {
        const fallbackClid = host.value === 'playgama.com' ? DEFAULT_CLIDS.direct : DEFAULT_CLIDS.directTest
        return (query.value?.[CLID_PARAM] as string) || landingClid || fallbackClid
    })

    const exportMode = computed((): ExportMode => {
        switch (query.value?.[EXPORT_MODE_PARAM]) {
            case 'portal':
                return 'portal'
            case 'widget':
                return 'widget'
        }
        return 'unknown'
    })

    const isPortal = computed(() => exportMode.value === 'portal')
    const isWidget = computed(() => exportMode.value === 'widget')

    const queryParams = computed(() => Object.entries(query.value || {}))

    const isAuthBannerOpen = ref<boolean>(false)

    function setPageStatus(status: PageStatus) {
        pageStatus.value = status
    }
    function setUserParams(details: { userAcquisitionParams: Record<string, string> }) {
        userAcquisitionParams.value = details.userAcquisitionParams
    }

    function setTargetURL(target: string) {
        targetURL.value = target
    }

    function setPlatform(plt: PlatformType) {
        platform.value = plt
    }

    function setUrlDetails(details: { query: LocationQuery; host: string }) {
        host.value = details.host
        query.value = details.query
    }

    function $reset() {
        pageStatus.value = 200
        targetURL.value = ''
        platform.value = DEFAULT_PLATFORM
    }

    // TODO remove this, and targetURL, setTargetURL, setPageStatus all together
    // use router methods instead
    const router = useRouter()
    watch(
        () => [pageStatus.value, targetURL.value],
        () => {
            if (targetURL.value !== '' && pageStatus.value > 300 && pageStatus.value < 400) {
                router.replace(targetURL.value)
                pageStatus.value = 200
                targetURL.value = ''
            }
        },
    )

    return {
        setPageStatus,
        setTargetURL,
        setUrlDetails,
        pageStatus,
        targetURL,
        userAcquisitionParams,
        clid,
        host,
        queryParams,
        query,
        widgetId,
        platform,
        exportMode,
        isAuthBannerOpen,
        isGameDistribution,
        isDebugMode,
        isWidget,
        isPortal,
        setPlatform,
        setUserParams,
        $reset,
    }
})
